import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { supabase } from "../lib/supabase"
import { getSupabaseFunctionErrorMessage } from "../utils/supabaseFunctionUtils"

interface UpdateStageParams {
  id: string
  stage: string
  segment?: string
  stage_reason?: string
  stage_note?: string
}

// Minimal shape of the updated application row returned by the Edge Function
type UpdatedApplicationRow = {
  id: string
  current_stage: string
  current_segment: string | null
  status: string
  stage_reason: string | null
  stage_note: string | null
}

/**
 * Hook for updating an application's stage/status using Supabase Edge Function
 */
export const useUpdateApplicationStage = () => {
  const queryClient = useQueryClient()

  return useMutation<UpdatedApplicationRow[] | null, unknown, UpdateStageParams>({
    mutationFn: async ({ id, stage, segment, stage_reason, stage_note }: UpdateStageParams) => {
      // Call the Supabase Edge Function to update the application stage
      const { data, error } = await supabase.functions.invoke("update-application-stage", {
        body: {
          id,
          stage,
          segment,
          stage_reason,
          stage_note,
        },
      })

      if (error) {
        throw new Error(await getSupabaseFunctionErrorMessage(error))
      }

      if (!data.success) {
        throw new Error(data.message || "Failed to update application stage")
      }

      return data.data
    },
    onSuccess: (_data, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["applications"] })
      // Also invalidate the specific application details query so details page refreshes
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: ["applicationDetails", variables.id],
        })
      }
    },
    onError: (error) => {
      console.error("Error updating application status:", error)
      toast.error("Update Failed", {
        description:
          error instanceof Error && error.message
            ? error.message
            : "Failed to update application status. Please try again.",
      })
    },
  })
}
